/** The host container for an activity surface: named, bounded, and framing its own states. */

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/cn";

import { EmptyState } from "../EmptyState/EmptyState";
import { ErrorState } from "../ErrorState/ErrorState";
import { Skeleton } from "../Skeleton/Skeleton";
import { lengthOf, mergeStyle } from "../internal/style";
import "./SurfaceSlot.css";

export interface SurfaceSlotProps {
  /** The activity's own name, and the accessible name of the region. */
  readonly name: string;
  readonly version?: ReactNode;
  readonly badge?: ReactNode;
  readonly state?: "content" | "empty" | "loading" | "error";
  readonly emptyText?: ReactNode;
  readonly errorText?: ReactNode;
  readonly minHeight?: string | number;
  readonly maxHeight?: string | number;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode;
}

export function SurfaceSlot({
  name,
  version,
  badge,
  state = "content",
  emptyText = "Nothing yet",
  errorText = "Not available",
  minHeight,
  maxHeight,
  className,
  style,
  children,
}: SurfaceSlotProps) {
  return (
    <section aria-label={name} className={cn("al-surfaceslot", className)} style={style}>
      <header className="al-surfaceslot-head">
        <h3 className="al-surfaceslot-name">{name}</h3>
        {version !== undefined ? (
          <span className="al-surfaceslot-version al-mono">{version}</span>
        ) : null}
        {badge !== undefined ? <span className="al-surfaceslot-badge">{badge}</span> : null}
      </header>
      <div
        className="al-surfaceslot-box"
        style={mergeStyle(undefined, {
          minHeight: lengthOf(minHeight),
          maxHeight: lengthOf(maxHeight),
        })}
      >
        {state === "loading" ? <Skeleton height="var(--al-space-9)" /> : null}
        {state === "empty" ? <EmptyState bare headline={emptyText} /> : null}
        {state === "error" ? <ErrorState headline={errorText} /> : null}
        {state === "content" ? children : null}
      </div>
    </section>
  );
}
